import { debounce, includes, isEqual, isUndefined, set } from "lodash";
import { map } from "lodash/collection";
import { isNumber } from "lodash/lang";
import { Component } from "react";
import leftIcon from "../assets/chevron_left.svg";
import menuBookIcon from "../assets/menu_book.svg";
import { Checkbox } from "../components/Checkbox";
import { FormSectionTile } from "../components/FormSectionTile";
import { HelperIcon } from "../components/HelperIcon";
import { TrimmingInput } from "../components/Input";
import { InstallationInstructionModal } from "../components/InstallationInstructionModal";
import { RadioGroup } from "../components/RadioGroup";
import { removeEmptyFieldsFromObject } from "../utils/objectHelpers";

const avatarFemale =
  "https://cdn.sentione.com/chat-ui-launcher/avatars/avatar-female.png";
const avatarMale =
  "https://cdn.sentione.com/chat-ui-launcher/avatars/avatar-male.png";
const avatarNeutral =
  "https://cdn.sentione.com/chat-ui-launcher/avatars/avatar-bot.svg";

const defaultDemoPageConfig = {
  backgroundPageURL: "https://sentione.com",
  backgroundSource: "page",
  appliedBackgroundPageUrl: "https://sentione.com",
  triggerBotMessage: "",
  triggerBotExtraData: "",
  welcomeUserMessage: "",
  settingsFormVisible: true,
};

const defaultWebchatConfig = {
  projectId: "",
  headerTitle: "SentiOne Chat",
  inputPlaceholder: "Type your message here...",
  endOfConversationLabel: "End of conversation",
  startNewConversationButtonLabel: "Start new conversation",

  botAvatar: avatarNeutral,
  showBotAvatarInHeader: false,
  showBotAvatarNearBubble: true,
  displayPoweredBy: true,

  agentAvatar: avatarMale,
  showAgentAvatarNearBubble: true,

  showMinimizeButton: true,
  showCloseButton: true,

  language: "en",
  userName: "Jan Kowalski",
  delayTime: 0,

  sourceType: "SentiOne-chat",

  message: "",
  extraData: "",
  initialMessageDelayTime: -1,

  silenceMessageText: "",
  silenceMessageExtraData: "",
  silenceMessageDelayTime: "",
  silenceMessageMax: "",

  everyMessageExtraData: "",

  customCss: "",
  customCssURL: "",
  themeColor: "",

  outerCustomCss: "",
  outerCustomCssURL: "",

  conversationWindowHeight: "",
  conversationWindowWidth: "",

  scrollTo: "bottom",

  announcementContent: "",
  announcementShow: true,
  announcementOnlyOnce: true,
  annotationMarkdownLinkBehavior: "new-tab",
  announcementClosable: true,
};

class DemoPage extends Component {
  constructor(props) {
    super(props);

    const formValues = {
      ...defaultDemoPageConfig,
      ...defaultWebchatConfig,
    };

    const queryParams = new URLSearchParams(window.location.search);
    const configToApply = {};

    queryParams.forEach((value, key) => {
      if (value.toLowerCase() === "true") {
        configToApply[key] = true;
        return;
      }

      if (value.toLowerCase() === "false") {
        configToApply[key] = false;
        return;
      }

      const numericValue = Number(value);

      if (!isNaN(numericValue) && isNumber(numericValue)) {
        configToApply[key] = numericValue;
        return;
      }

      configToApply[key] = value;
    });

    const initConfig = {
      ...formValues,
      ...configToApply,
    };

    this.state = {
      formValues: initConfig,
      settingsFormVisible: initConfig.settingsFormVisible,
      appliedBackgroundPageUrl: initConfig.backgroundPageURL,
      isChatOpen: false,
      isConversationStarted: false,
      isChatInitialized: false,
    };
  }

  componentDidMount() {
    if (this.state.formValues.projectId && this.state.formValues.headerTitle) {
      if (
        localStorage.getItem("projectId") !== this.state.formValues.projectId
      ) {
        localStorage.setItem("projectId", this.state.formValues.projectId);
      }
      this.initWebChat(this.state.formValues);
    }
  }

  updateQueryParams = () => {
    const queryParams = new URLSearchParams(window.location.search);
    const { formValues } = this.state;
    console.log(141, formValues);
    const settingChanges = Object.keys(formValues).reduce((acc, configKey) => {
      const defaultConfig = {
        ...defaultDemoPageConfig,
        ...defaultWebchatConfig,
      };

      if (
        !isEqual(formValues[configKey], defaultConfig[configKey]) &&
        !isUndefined(defaultConfig[configKey])
      ) {
        acc[configKey] = formValues[configKey];
      } else {
        queryParams.delete(configKey);
      }

      return acc;
    }, {});

    Object.keys(settingChanges).forEach((key) => {
      queryParams.set(key, settingChanges[key]);
    });

    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}?${queryParams.toString()}`
    );
  };

  onInputChange = (path, value) => {
    const updatedFormValues = { ...this.state.formValues };

    set(updatedFormValues, path, value);

    this.setState(
      {
        formValues: updatedFormValues,
      },
      () => this.debouncedUpdateQueryParams()
    );
  };

  debouncedUpdateQueryParams = debounce(this.updateQueryParams, 200);

  getChatSrcURL = () => {
    if (window.origin.includes("localhost")) {
      return "http://localhost:5761/fullscreen";
    }

    return `${window.origin}/fullscreen`;
  };

  setBackgroundPageURL = () => {
    const url = this.state.formValues.backgroundPageURL;

    this.setState({
      appliedBackgroundPageUrl: url,
    });
  };

  destroy = () => {
    window.sentiOneChat.destroy();
  };

  mapFormToConfig = (form) => {
    if (this.state.formValues.previewMode) {
      const obj = {};

      map(this.state.formValues, (rule, key) => {
        set(obj, key, rule);
      });

      return obj;
    }

    return {
      chatSrc: this.getChatSrcURL(),
      projectId: form.projectId,
      language: form.language,
      author: form.userName,
      sourceType: form.sourceType,

      appearance: {
        headerTitle: form.headerTitle,
        displayPoweredBy: form.displayPoweredBy,
        agentAvatar: {
          src: form.agentAvatar,
          showNearBubble: form.showAgentAvatarNearBubble,
        },
        avatar: {
          src: form.botAvatar,
          showInHeader: form.showBotAvatarInHeader,
          showNearBubble: form.showBotAvatarNearBubble,
        },
        inputPlaceholder: form.inputPlaceholder,

        outerStyles: {
          customCss: form.outerCustomCss,
          url: form.outerCustomCssURL,
        },

        conversationWindowHeight: form.conversationWindowHeight,
        conversationWindowWidth: form.conversationWindowWidth,

        endOfConversationLabel: form.endOfConversationLabel,
        startNewConversationButtonLabel: form.startNewConversationButtonLabel,

        styles: {
          customCss: form.customCss,
          url: form.customCssURL,
          themeColor: form.themeColor,
        },
      },

      actionButtons: {
        conversationWindow: {
          minimize: form.showMinimizeButton,
          resetAndMinimize: form.showCloseButton,
        },
      },

      initialMessage:
        form.message || form.extraData
          ? {
              text: form.message || "",
              extraData: this.safeJsonParse(form.extraData || null),
              delayTime: form.initialMessageDelayTime,
            }
          : null,

      autoStart: {
        delayTime: form.delayTime * 1000,
      },

      silenceMessage:
        form.silenceMessageDelayTime ||
        form.silenceMessageExtraData ||
        form.silenceMessageText
          ? {
              text: form?.silenceMessageText || "",
              extraData: this.safeJsonParse(
                form?.silenceMessageExtraData || null
              ),
              delayTime: form?.silenceMessageDelayTime || "",
              maxRepetition: form?.silenceMessageMax || undefined,
            }
          : null,

      everyMessage: {
        extraData: this.safeJsonParse(form.everyMessageExtraData || null),
      },

      session: {
        hostSessionCookie: true,
      },

      scrollOptions: {
        scrollTo: form.scrollTo,
      },

      disableWebsocket: form.disableWebsocket,

      announcementBanner: {
        content: form.announcementContent,
        markdownLinkBehavior: form.annotationMarkdownLinkBehavior,
        onlyOnce: form.announcementOnlyOnce,
        closable: form.announcementClosable,
        show: form.announcementShow,
      },
    };
  };

  safeJsonParse = (text) => {
    try {
      return JSON.parse(text || null);
    } catch {}
  };

  initWebChat = (form) => {
    if (form.headerTitle && form.projectId) {
      console.log(323, this.mapFormToConfig(form));
      const WEBCHAT_CONFIG = {
        ...this.mapFormToConfig(form),
        onOpenChat: () => {
          this.setState({
            isConversationStarted: true,
            isChatOpen: true,
          });
        },
        onCloseChat: () => {},
        onInitializedChat: () => {
          this.setState({
            isChatInitialized: true,
          });
        },
      };

      window.sentiOneChat.initWebChat(WEBCHAT_CONFIG);

      this.setState({
        isConversationStarted: !this.state.isConversationStarted,
      });
    }
  };

  webchatSettingsFormSubmit = (e) => {
    e.preventDefault();
    this.destroy();

    setTimeout(() => {
      this.clearCustomStyles();

      this.initWebChat(this.state.formValues);

      localStorage.setItem("projectId", this.state.formValues.projectId);
    }, 1000);
  };

  clearCustomStyles = () => {
    document
      .querySelectorAll("#socui-outer-custom-styles")
      .forEach((customStyles) => {
        customStyles.remove();
      });

    document
      .querySelectorAll("#socui-outer-custom-styles-url")
      .forEach((customStyles) => {
        customStyles.remove();
      });
  };

  getAvatarToPick = (avatar, fieldName) => {
    return (
      <label className="avatar-label">
        <input
          onChange={(e) => this.onInputChange(fieldName, e.target.value)}
          type="radio"
          name={fieldName}
          value={avatar}
          checked={this.state.formValues[fieldName] === avatar}
        />
        <img src={avatar} alt="" />
      </label>
    );
  };

  getBackgroundPageURL = () => {
    const url = this.state.appliedBackgroundPageUrl;

    const backgroundPageURL = includes(url, "sentione.com")
      ? `${url}?noIntercom=true`
      : url;

    return backgroundPageURL;
  };

  openInstallationInstructionModal = () => {
    const dialog = document.querySelector("dialog");
    dialog.showModal();
  };

  getChatUiLauncherUrl = () => {
    return document.querySelector("#chat-ui-launcher-url").src;
  };

  getWebchatCode = () => {
    const config = this.mapFormToConfig(this.state.formValues);

    const code = `<script src="${this.getChatUiLauncherUrl()}" type="application/javascript"></script>

<script> 
    window.sentiOneChat.initWebChat(${JSON.stringify(
      removeEmptyFieldsFromObject(config),
      null,
      "        "
    )})
</script>`;

    return code;
  };

  render() {
    const { settingsFormVisible, formValues } = this.state;

    return (
      <div className={"demo-company"}>
        <div
          className={`sidebar ${settingsFormVisible ? "" : "sidebar--hidden"} ${
            formValues.previewMode ? "sidebar--no-sidebar" : ""
          }`}
        >
          <div className={"sidebar__content"}>
            <section>
              <h1>Webchat configuration</h1>

              <form
                className="form"
                id={"setting-form"}
                onSubmit={this.webchatSettingsFormSubmit}
              >
                <div className="form__section">
                  <label htmlFor="projectId">
                    Project ID
                    <HelperIcon
                      title={"Enter ID of the project from Automate"}
                    />
                  </label>

                  <div>
                    <TrimmingInput
                      type="text"
                      name="projectId"
                      id="projectId"
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      onChangeOrBlur={this.onInputChange}
                      value={formValues.projectId}
                      required
                      autoFocus={!formValues.projectId}
                    />
                  </div>
                </div>

                <FormSectionTile
                  icon={"color_lens"}
                  title={"Appearance"}
                  description={"Customize how webchat looks"}
                >
                  <div className={"form__section"}>
                    <label htmlFor="headerTitle">Header title</label>
                    <div>
                      <TrimmingInput
                        type="text"
                        name={"headerTitle"}
                        placeholder="SentiOne Chat"
                        value={formValues.headerTitle}
                        required
                        onChangeOrBlur={this.onInputChange}
                      />
                    </div>
                  </div>
                  <div className={"form__section"}>
                    <label htmlFor="headerTitle">Input placeholder</label>
                    <div>
                      <TrimmingInput
                        type="text"
                        name={"inputPlaceholder"}
                        placeholder="Type your message here..."
                        value={formValues.inputPlaceholder}
                        required
                        onChangeOrBlur={this.onInputChange}
                      />
                    </div>
                  </div>
                  <div className={"form__section"}>
                    <label htmlFor="headerTitle">
                      End of conversation label
                    </label>
                    <div>
                      <TrimmingInput
                        type="text"
                        name={"endOfConversationLabel"}
                        placeholder="End of conversation"
                        value={formValues.endOfConversationLabel}
                        required
                        onChangeOrBlur={this.onInputChange}
                      />
                    </div>
                  </div>
                  <div className={"form__section"}>
                    <label htmlFor="headerTitle">
                      Start new conversation button label
                    </label>
                    <div>
                      <TrimmingInput
                        type="text"
                        name={"startNewConversationButtonLabel"}
                        placeholder="Start new conversation"
                        value={formValues.startNewConversationButtonLabel}
                        required
                        onChangeOrBlur={this.onInputChange}
                      />
                    </div>
                  </div>
                  <div className={"form__section"}>
                    <label htmlFor="headerTitle">Custom CSS URL</label>

                    <textarea
                      rows={2}
                      name={"customCssURL"}
                      placeholder="https://cdn.sentione.com/chat-ui-launcher/themes/dark-blue.css"
                      value={formValues.customCssURL}
                      onChange={(e) =>
                        this.onInputChange("customCssURL", e.target.value)
                      }
                      onBlur={(e) => {
                        this.onInputChange(
                          "customCssURL",
                          e.target.value.trim()
                        );
                      }}
                    />
                  </div>
                  <div className={"form__section"}>
                    <label htmlFor="headerTitle">Custom CSS</label>

                    <textarea
                      rows={2}
                      name={"customCss"}
                      placeholder=".chat-header__title {color: white;}"
                      value={formValues.customCss}
                      onChange={(e) =>
                        this.onInputChange("customCss", e.target.value)
                      }
                      onBlur={(e) => {
                        this.onInputChange("customCss", e.target.value.trim());
                      }}
                    />
                  </div>
                  <div className={"form__section"}>
                    <label htmlFor="headerTitle">Theme main color</label>
                    <div>
                      <TrimmingInput
                        type="text"
                        name={"themeColor"}
                        placeholder="#2f94ff"
                        value={formValues.themeColor}
                        onChangeOrBlur={this.onInputChange}
                      />
                    </div>
                  </div>
                  <div className={"form__section"}>
                    <label htmlFor="conversationWindowHeight">
                      Conversation window height
                    </label>
                    <div>
                      <TrimmingInput
                        type="text"
                        name={"conversationWindowHeight"}
                        placeholder="200px"
                        value={formValues.conversationWindowHeight}
                        onChangeOrBlur={this.onInputChange}
                      />
                    </div>
                  </div>
                  <div className={"form__section"}>
                    <label htmlFor="conversationWindowWidth">
                      Conversation window width
                    </label>
                    <div>
                      <TrimmingInput
                        type="text"
                        name={"conversationWindowWidth"}
                        placeholder="200px"
                        value={formValues.conversationWindowWidth}
                        onChangeOrBlur={this.onInputChange}
                      />
                    </div>
                  </div>
                  <div className={"form__section"}>
                    <label htmlFor="headerTitle">
                      Outer custom CSS URL{" "}
                      <HelperIcon
                        title={
                          "CSS styles will be injected to host page of the webchat. You can style with this property, among others, position of " +
                          "webchat trigger button and conversation window. With proper CSS you can set that, " +
                          "webchat will open on the left side of the webpage. You can not style any inner element of the webchat with this property."
                        }
                      />
                    </label>
                    <div>
                      <textarea
                        rows={2}
                        name={"outerCustomCssURL"}
                        placeholder=""
                        value={formValues.outerCustomCssURL}
                        onChange={(e) =>
                          this.onInputChange(
                            "outerCustomCssURL",
                            e.target.value
                          )
                        }
                        onBlur={(e) => {
                          this.onInputChange(
                            "outerCustomCssURL",
                            e.target.value.trim()
                          );
                        }}
                      />
                    </div>
                  </div>
                  <div className={"form__section"}>
                    <label htmlFor="headerTitle">
                      Outer custom CSS{" "}
                      <HelperIcon
                        title={
                          "Styles from URL will be injected to host page of the webchat. You can style with this property, among others, position of " +
                          "webchat trigger button and conversation window. With proper CSS you can set that, " +
                          "webchat will open on the left side of the webpage. You can not style any inner element of the webchat with this property."
                        }
                      />
                    </label>
                    <div>
                      <textarea
                        rows={2}
                        name={"outerCustomCss"}
                        placeholder=".socui-chat__button-iframe {right: 500px} .socui-chat__window {right: 500px}"
                        value={formValues.outerCustomCss}
                        onChange={(e) =>
                          this.onInputChange("outerCustomCss", e.target.value)
                        }
                        onBlur={(e) => {
                          this.onInputChange(
                            "outerCustomCss",
                            e.target.value.trim()
                          );
                        }}
                      />
                    </div>
                  </div>
                  <div className={"form__section"}>
                    <label>Agent's avatar</label>
                    <div>
                      <div
                        className={"avatars-container avatars-container__agent"}
                      >
                        {this.getAvatarToPick(avatarMale, "agentAvatar")}
                        {this.getAvatarToPick(avatarFemale, "agentAvatar")}
                        {this.getAvatarToPick(avatarNeutral, "agentAvatar")}
                      </div>
                    </div>

                    <div className="form__section--small-padding">
                      <Checkbox
                        value={JSON.parse(formValues.showAgentAvatarNearBubble)}
                        label={"Show avatar next to agent response bubble"}
                        isReadOnly={false}
                        onChange={(value) =>
                          this.onInputChange("showAgentAvatarNearBubble", value)
                        }
                      />
                    </div>
                  </div>
                  <div className="form__section">
                    <label>Bot's avatar</label>
                    <div>
                      <div
                        className={"avatars-container avatars-container__bot"}
                      >
                        {this.getAvatarToPick(avatarNeutral, "botAvatar")}
                        {this.getAvatarToPick(avatarFemale, "botAvatar")}
                        {this.getAvatarToPick(avatarMale, "botAvatar")}
                      </div>
                    </div>
                  </div>
                  <div className={"form__section"}>
                    <label>Avatar placement</label>

                    <div className="form__section--small-padding">
                      <Checkbox
                        value={JSON.parse(formValues.showBotAvatarNearBubble)}
                        label={"Show avatar next to response bubble"}
                        isReadOnly={false}
                        onChange={(value) =>
                          this.onInputChange("showBotAvatarNearBubble", value)
                        }
                      />
                    </div>
                    <div className="form__section--small-padding">
                      <Checkbox
                        value={formValues.showBotAvatarInHeader}
                        label={"Show avatar in header"}
                        isReadOnly={false}
                        onChange={(value) =>
                          this.onInputChange("showBotAvatarInHeader", value)
                        }
                      />
                    </div>
                    <div className="form__section--small-padding">
                      <Checkbox
                        value={formValues.displayPoweredBy}
                        label={"Display 'Powered by SentiOne'"}
                        isReadOnly={false}
                        onChange={(value) =>
                          this.onInputChange("displayPoweredBy", value)
                        }
                      />
                    </div>
                  </div>
                </FormSectionTile>

                <FormSectionTile
                  icon={"open_in_browser"}
                  title={"Auto opening"}
                  description={"Customize when webchat should open"}
                >
                  <div className={"form__section"}>
                    <label htmlFor="headerTitle">
                      Open delay time (seconds)
                    </label>
                    <div>
                      <TrimmingInput
                        type="number"
                        name={"delayTime"}
                        placeholder="0"
                        value={formValues.delayTime}
                        required
                        onChangeOrBlur={this.onInputChange}
                      />
                    </div>
                  </div>
                </FormSectionTile>

                <FormSectionTile
                  icon={"waving_hand"}
                  title={"Initial Message"}
                  description={
                    "Customize how webchat should start conversation"
                  }
                >
                  <div className={"form__section"}>
                    <label htmlFor="headerTitle">
                      Initial message
                      <HelperIcon
                        title={
                          "Provide a message that will be used to start the conversation with bot"
                        }
                      />
                    </label>
                    <div>
                      <TrimmingInput
                        type="text"
                        name={"message"}
                        placeholder=""
                        value={formValues.message}
                        onChangeOrBlur={this.onInputChange}
                      />
                    </div>
                  </div>
                  <div className={"form__section"}>
                    <label htmlFor="headerTitle">
                      Initial extra data (JSON Object)
                    </label>
                    <div>
                      <TrimmingInput
                        type="text"
                        name={"extraData"}
                        placeholder='{"process": "ABC"}'
                        value={formValues.extraData}
                        onChangeOrBlur={this.onInputChange}
                      />
                    </div>
                  </div>
                  <div className={"form__section"}>
                    <label htmlFor="headerTitle">
                      Delay Time
                      <HelperIcon
                        title={
                          "When value is bigger that 0, webchat will start conversation also when chat is not opened yet. "
                        }
                      />
                    </label>
                    <div>
                      <TrimmingInput
                        type="text"
                        name={"initialMessageDelayTime"}
                        placeholder=""
                        value={formValues.initialMessageDelayTime}
                        onChangeOrBlur={this.onInputChange}
                      />
                    </div>
                  </div>
                </FormSectionTile>

                <FormSectionTile
                  icon={"notifications_paused"}
                  title={"Silence message"}
                  description={"Customize when webchat should send reminder"}
                >
                  <div className={"form__section"}>
                    <label htmlFor="headerTitle">
                      Delay time{" "}
                      <HelperIcon
                        title={
                          "Provide a value that webchat will wait for user action, after this period silence message will be send"
                        }
                      />
                    </label>

                    <div>
                      <TrimmingInput
                        type="text"
                        name={"silenceMessageDelayTime"}
                        placeholder="5000"
                        value={formValues.silenceMessageDelayTime}
                        onChangeOrBlur={this.onInputChange}
                      />
                    </div>
                  </div>
                  <div className={"form__section"}>
                    <label htmlFor="headerTitle">
                      Text
                      <HelperIcon
                        title={
                          "Provide a message that will be send to bot to trigger silence message. Text OR ExtraData is required"
                        }
                      />
                    </label>

                    <TrimmingInput
                      type="text"
                      name={"silenceMessageText"}
                      placeholder=""
                      value={formValues.silenceMessageText}
                      onChangeOrBlur={this.onInputChange}
                    />
                  </div>
                  <div className={"form__section"}>
                    <label htmlFor="headerTitle">
                      Extra data{" "}
                      <HelperIcon
                        title={
                          "Provide a object that will be send to bot to trigger silence message. Text OR ExtraData is required"
                        }
                      />
                    </label>

                    <TrimmingInput
                      type="text"
                      name={"silenceMessageExtraData"}
                      placeholder='{"process": "ABC"}'
                      value={formValues.silenceMessageExtraData}
                      onChangeOrBlur={this.onInputChange}
                    />
                  </div>
                  <div className={"form__section"}>
                    <label htmlFor="headerTitle">
                      Max repetition
                      <HelperIcon
                        title={
                          "Provide a number how many reminder messages user can see "
                        }
                      />
                    </label>
                    <div>
                      <TrimmingInput
                        type="text"
                        name={"silenceMessageMax"}
                        placeholder=""
                        value={formValues.silenceMessageMax}
                        onChangeOrBlur={this.onInputChange}
                      />
                    </div>
                  </div>
                </FormSectionTile>

                <FormSectionTile
                  icon={"sms"}
                  title={"Extra data"}
                  description={"Send extra data with every message"}
                >
                  <div className={"form__section"}>
                    <label htmlFor="headerTitle">
                      Extra data{" "}
                      <HelperIcon
                        title={
                          "Provide extra data (as object) that webchat will send with user message to API"
                        }
                      />
                    </label>

                    <div>
                      <TrimmingInput
                        type="text"
                        name={"everyMessageExtraData"}
                        value={formValues.everyMessageExtraData}
                        onChangeOrBlur={this.onInputChange}
                      />
                    </div>
                  </div>
                </FormSectionTile>

                <FormSectionTile
                  icon={"dialpad"}
                  title={"Action buttons"}
                  description={"Customize additional buttons"}
                >
                  <div className="form__section">
                    <div className="form__section--small-padding">
                      <Checkbox
                        value={formValues.showMinimizeButton}
                        label={"Show minimize button in header"}
                        isReadOnly={false}
                        onChange={(value) =>
                          this.onInputChange("showMinimizeButton", value)
                        }
                      />
                    </div>

                    <div className="form__section--small-padding">
                      <Checkbox
                        value={formValues.showCloseButton}
                        label={"Show close button in header"}
                        isReadOnly={false}
                        onChange={(value) =>
                          this.onInputChange("showCloseButton", value)
                        }
                      />
                    </div>
                  </div>
                </FormSectionTile>

                <FormSectionTile
                  icon={"campaign"}
                  title={"Announcement Banner"}
                >
                  <div className={"form__section"}>
                    <label htmlFor="announcementContent">Content</label>

                    <div>
                      <TrimmingInput
                        type="text"
                        name={"announcementContent"}
                        value={formValues.announcementContent}
                        onChangeOrBlur={this.onInputChange}
                      />
                    </div>
                  </div>
                  <div className={"form__section"}>
                    <label htmlFor="language">Language</label>

                    <div>
                      <select
                        onChange={(e) => {
                          this.onInputChange(
                            "annotationMarkdownLinkBehavior",
                            e.target.value
                          );
                        }}
                        data-test-id={"select-annotationMarkdownLinkBehavior"}
                        value={formValues.annotationMarkdownLinkBehavior}
                      >
                        <option value={"new-tab"}>New tab</option>
                        <option value={"callback"}>Callback function</option>
                      </select>
                    </div>
                  </div>

                  <div className={"form__section"}>
                    <div className="form__section--small-padding">
                      <Checkbox
                        value={JSON.parse(formValues.announcementOnlyOnce)}
                        label={"Show banner until user close it"}
                        isReadOnly={false}
                        onChange={(value) =>
                          this.onInputChange("announcementOnlyOnce", value)
                        }
                      />
                    </div>

                    <div className="form__section--small-padding">
                      <Checkbox
                        value={JSON.parse(formValues.announcementClosable)}
                        label={"User can close banner"}
                        isReadOnly={false}
                        onChange={(value) =>
                          this.onInputChange("announcementClosable", value)
                        }
                      />
                    </div>

                    <div className="form__section--small-padding">
                      <Checkbox
                        value={JSON.parse(formValues.announcementShow)}
                        label={"Display banner"}
                        isReadOnly={false}
                        onChange={(value) =>
                          this.onInputChange("announcementShow", value)
                        }
                      />
                    </div>
                  </div>
                </FormSectionTile>

                <FormSectionTile icon={"settings"} title={"More"}>
                  <div className={"form__section"}>
                    <label htmlFor="language">Language</label>

                    <div>
                      <select
                        onChange={(e) => {
                          this.onInputChange("language", e.target.value);
                        }}
                        data-test-id={"select-language"}
                        value={formValues.language}
                      >
                        <option value={"en"}>English</option>
                        <option value={"pl"}>Polish</option>
                      </select>
                    </div>
                  </div>
                  <div className={"form__section"}>
                    <label htmlFor="userName">Username</label>
                    <div>
                      <TrimmingInput
                        type="text"
                        name={"userName"}
                        placeholder="John Doe"
                        value={formValues.userName}
                        onChangeOrBlur={this.onInputChange}
                      />
                    </div>
                  </div>
                  <div className={"form__section"}>
                    <label htmlFor="headerTitle">Source type</label>
                    <div>
                      <TrimmingInput
                        type="text"
                        name={"sourceType"}
                        placeholder="SentiOne-chat"
                        value={formValues.sourceType}
                        onChangeOrBlur={this.onInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div className={"form__section"}>
                    <label htmlFor="headerTitle">Scroll To</label>
                    <div>
                      <select
                        onChange={(e) => {
                          this.onInputChange("scrollTo", e.target.value);
                        }}
                        data-test-id={"scrollTo"}
                        value={formValues.scrollTo}
                      >
                        <option value={"bottom"}>
                          Bottom of conversation window
                        </option>
                        <option value={"topOfMessage"}>
                          Top of long message
                        </option>
                      </select>
                    </div>
                  </div>
                </FormSectionTile>

                <div className="form__section form__section--text-right form__section--sticky">
                  <button
                    className="btn btn--secondary"
                    onClick={this.openInstallationInstructionModal}
                  >
                    Get the code
                  </button>
                  <button className="btn" type="submit">
                    Setup webchat
                  </button>
                </div>
              </form>
            </section>

            <hr />

            <section>
              <h1>Demo page settings</h1>
              <form
                className="form"
                id={"background-form"}
                onSubmit={(e) => {
                  e.preventDefault();
                  if ("activeElement" in document)
                    document.activeElement.blur();
                }}
              >
                <div className="form__section ">
                  <label htmlFor="background-page">Background source</label>

                  <RadioGroup
                    name={"background-source"}
                    value={formValues.backgroundSource}
                    options={[
                      { value: "page", label: "Page URL" },
                      { value: "image", label: "Image URL" },
                    ]}
                    onChange={(value) => {
                      this.onInputChange("backgroundSource", value);
                    }}
                  />
                </div>

                <div className="form__section ">
                  <label htmlFor="background-page">
                    {formValues.backgroundSource === "image" ? (
                      <div>
                        Image URL
                        <span className="form__label-help">
                          <a
                            href="https://automate.help.sentione.com/docs/where-i-can-test-it#image-as-a-background"
                            target={"_blank"}
                            rel="noreferrer"
                          >
                            (read more)
                          </a>
                        </span>
                      </div>
                    ) : (
                      <div>Page URL</div>
                    )}
                  </label>
                  <div>
                    <input
                      onKeyUp={() => {
                        this.setBackgroundPageURL();
                      }}
                      type="text"
                      name={"background-url"}
                      id="background-page"
                      placeholder="https://sentione.com"
                      value={formValues.backgroundPageURL}
                      onChange={(e) =>
                        this.onInputChange("backgroundPageURL", e.target.value)
                      }
                    />
                  </div>
                </div>
              </form>
            </section>
          </div>

          <div className="sidebar__footer">
            <span className="sidebar__footer-copyrights">
              Powered by
              <a
                className={"sidebar__sentione-link"}
                href="https://sentione.com/"
                target="_blank"
                rel="noreferrer"
              >
                SentiOne
              </a>
            </span>

            <a
              className={"sidebar__sentione-link"}
              href={"https://automate.help.sentione.com/"}
              target={"_blank"}
              rel={"noreferrer"}
              title={"See docs"}
            >
              <img src={menuBookIcon} alt={""} className={"readme-icon"} />
            </a>

            <span
              className={"sidebar__section-toggle"}
              onClick={() => {
                this.setState(
                  {
                    settingsFormVisible: !settingsFormVisible,
                  },
                  () => {
                    this.onInputChange(
                      "settingsFormVisible",
                      !settingsFormVisible
                    );
                  }
                );
              }}
            >
              <img src={leftIcon} alt="" title={"Close chat settings"} />
            </span>
          </div>
        </div>
        {formValues.previewMode ? null : (
          <div className={"demo-company__page"}>
            {this.state.formValues.backgroundSource === "image" ? (
              <img src={this.state.appliedBackgroundPageUrl} alt="" />
            ) : (
              <iframe
                rel="noreferrer"
                title={"background-page"}
                id={"background-page"}
                src={this.getBackgroundPageURL()}
                frameBorder="0"
              />
            )}
          </div>
        )}
        <InstallationInstructionModal code={this.getWebchatCode()} />
      </div>
    );
  }
}

export { DemoPage };
